import { getDate, isEqual } from "date-fns";
import { EventMenu } from "@circle-react/components/Events/ListViewItem/EventMenu";
import { RsvpActionButton } from "@circle-react/components/Events/NewEventsSpace/EventDetailPage/RsvpActionButton";
import { EventLocation } from "@circle-react/components/Events/NewEventsSpace/ListViewItem/EventLocation";
import { ListViewEventTimestamp } from "@circle-react/components/Events/NewEventsSpace/ListViewItem/ListViewEventTimestamp";
import { RsvpWarningMessages } from "@circle-react/components/Events/NewEventsSpace/shared";
import {
  canRespondToInvitation,
  isEventFinished,
} from "@circle-react/components/Events/NewEventsSpace/shared/helpers";
import { STATUS } from "@circle-react/components/Events/helpers/constants";
import { ShowPageLink } from "@circle-react/components/PostsV3/PostViewListItem/ShowPageLink";
import { useCurrentPostContext } from "@circle-react/contexts";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useSmOrMdScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import type { Event } from "@circle-react/types/Event";
import { SkeletonLoader } from "@circle-react-uikit/SkeletonLoader";
import { useEvent } from "../../hooks/useEvent";

interface Props {
  onChange: (event: Event) => Promise<void>;
  onDestroy: (eventId: number) => void;
}

export const EventItem = ({ onDestroy, onChange }: Props) => {
  const { record: eventInfo } = useCurrentPostContext();
  const { eventData: eventDetail, isLoading } = useEvent({ event: eventInfo });
  const isSmallScreen = useSmOrMdScreenMediaQuery();

  if (isLoading)
    return (
      <div className="border-primary bg-primary flex w-full flex-col border-b p-4 last:border-none md:h-auto md:flex-row">
        <SkeletonLoader variant="event-item" isMobileView />
      </div>
    );

  const event = { ...eventInfo, ...eventDetail };
  const { name, status, event_setting_attributes: eventData } = event;
  const {
    starts_at: startsAt,
    ends_at: endsAt,
    location_type: locationType,
  } = eventData || {};

  const isPublished = status === STATUS.PUBLISHED;
  const shouldDisplayRsvpAction =
    isPublished && !isEventFinished(event) && canRespondToInvitation(event);

  const isOneDayEvent = isEqual(
    getDate(new Date(startsAt)),
    getDate(new Date(endsAt)),
  );

  return (
    <div className="border-primary bg-primary flex w-full flex-col border-b p-4 last:border-none md:h-auto md:flex-row">
      <div className="flex w-full justify-between md:ml-6">
        <div className="flex flex-col justify-center">
          <ShowPageLink
            post={event}
            className="!text-dark mb-2 !line-clamp-2 max-w-[400px] break-words text-base font-semibold leading-5 md:mb-1"
          >
            {name}
          </ShowPageLink>
          <div
            className={classNames({
              "flex flex-col items-start": !isOneDayEvent || isSmallScreen,
              "flex flex-row items-center space-x-3":
                isOneDayEvent && !isSmallScreen,
            })}
          >
            <div className="my-1 flex items-center gap-x-2 md:my-0">
              <ListViewEventTimestamp event={event} />
            </div>
            <EventLocation type={locationType} />
          </div>
          <RsvpWarningMessages event={event} />
        </div>
        <div className="flex items-start space-x-4 md:items-center">
          <EventMenu event={event} onDestroy={onDestroy} />
        </div>
      </div>
      {shouldDisplayRsvpAction && (
        <div className="mt-6">
          <RsvpActionButton
            event={event}
            onChange={onChange}
            variant="secondary"
          />
        </div>
      )}
    </div>
  );
};
